import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface SearchResult {
  title: string;
  description: string;
  url: string;
  category: string;
  tags: string[];
}

const searchIndex: SearchResult[] = [
  // Solutions
  { title: "Surveillance & Security Solutions", description: "Body-worn cameras and mission-critical communications from Hytera.", url: "/solutions/surveillance", category: "Solutions", tags: ["body camera", "bwv", "surveillance", "security", "hytera", "law enforcement"] },
  { title: "Rugged Computing Solutions", description: "MIL-SPEC laptops, tablets, and handheld computers from Honeywell and Dell Rugged Pro.", url: "/solutions/rugged-computing", category: "Solutions", tags: ["rugged", "tablet", "laptop", "honeywell", "dell", "mil-spec", "warehouse", "field", "handheld"] },
  { title: "HCI & Storage Infrastructure", description: "Replace VMware and Nutanix with Arcfra — complete hyperconverged infrastructure and data protection.", url: "/solutions/data-protection", category: "Solutions", tags: ["hci", "storage", "arcfra", "exagrid", "synology", "vmware", "nutanix", "backup", "data protection", "hyperconverged"] },
  { title: "Solutions Overview", description: "Explore all SE Distribution solutions across surveillance, rugged computing, and infrastructure.", url: "/solutions", category: "Solutions", tags: ["solutions", "overview"] },

  // Products — Hytera BWV
  { title: "Hytera SC880 5G Body Camera", description: "5G flagship body camera — 4K video, 300s pre-record, 10-hr battery, IP68, MIL-STD-810G.", url: "/hytera-bwv", category: "Products", tags: ["sc880", "5g", "body camera", "hytera", "4k", "bwv", "law enforcement"] },
  { title: "Hytera SC780 4G Body Camera", description: "4G LTE smart body camera — 1080P, 130° FOV, 13-hr battery, 120s pre-record, IP68.", url: "/hytera-bwv", category: "Products", tags: ["sc780", "4g", "body camera", "hytera", "bwv"] },
  { title: "Hytera GC550 Body Camera", description: "Compact body camera — 1080P, 8-hr battery, IP67, infrared night vision, AES-256 encryption.", url: "/hytera-bwv", category: "Products", tags: ["gc550", "compact camera", "hytera", "bwv", "night vision"] },
  { title: "Hytera DEMS Evidence Platform", description: "Digital evidence management — chain-of-custody, AI search, audit trail, cloud upload.", url: "/hytera-bwv", category: "Products", tags: ["dems", "evidence", "hytera", "digital evidence", "chain of custody"] },
  { title: "Hytera BWC Spotlight", description: "Hytera body-worn camera lineup — SC880 5G, SC780 4G, GC550 & DEMS evidence platform.", url: "/hytera-bwv", category: "Spotlight", tags: ["hytera", "bwc", "body worn camera", "spotlight", "body camera"] },

  // Products — Rugged
  { title: "Dell Pro Rugged 10 Tablet", description: "World's lightest 10\" fully-rugged Windows tablet — 1.04kg, IP66, Intel Core Ultra 200V, Wi-Fi 7.", url: "/rugged-spotlight", category: "Products", tags: ["dell", "rugged", "tablet", "10 inch", "windows", "mil-spec"] },
  { title: "Dell Pro Rugged 12 Tablet", description: "12\" fully-rugged tablet — 1200-nit, dual hot-swap batteries, Intel Core Ultra 200V, Wi-Fi 7.", url: "/rugged-spotlight", category: "Products", tags: ["dell", "rugged", "tablet", "12 inch", "windows"] },
  { title: "Dell Pro Rugged 13 Laptop", description: "World's smallest 13\" fully-rugged AI PC — 1400-nit, IP65, 6ft drop-tested, optional 5G.", url: "/rugged-spotlight", category: "Products", tags: ["dell", "rugged", "laptop", "13 inch", "mil-spec"] },
  { title: "Dell Pro Rugged 14 Laptop", description: "14\" semi-rugged AI PC — optional NVIDIA RTX 500 Ada, 1100-nit, IP53.", url: "/rugged-spotlight", category: "Products", tags: ["dell", "rugged", "laptop", "14 inch"] },
  { title: "Honeywell ScanPal EDA10A", description: "10-inch Android enterprise tablet — 5G, Wi-Fi 6, IP65, MIL-STD-810H, 8000mAh.", url: "/rugged-spotlight", category: "Products", tags: ["honeywell", "scanpal", "eda10a", "tablet", "android", "rugged"] },
  { title: "Honeywell RT10 Android Tablet", description: "10-inch Android rugged tablet — FlexRange scanner, hot-swap battery, 4G LTE CAT13.", url: "/rugged-spotlight", category: "Products", tags: ["honeywell", "rt10", "android", "tablet", "rugged", "flexrange"] },
  { title: "Honeywell RT10 Windows Tablet", description: "10-inch Windows 10 Enterprise LTSC rugged tablet — FlexRange scanner, vehicle dock ready.", url: "/rugged-spotlight", category: "Products", tags: ["honeywell", "rt10", "windows", "tablet", "rugged"] },
  { title: "Rugged Computing Spotlight", description: "Full Honeywell and Dell Rugged Pro device lineup — compare specs and find the right device.", url: "/rugged-spotlight", category: "Spotlight", tags: ["rugged", "spotlight", "honeywell", "dell", "tablet", "laptop"] },

  // Products — Storage
  { title: "Arcfra ArcHCI Platform", description: "VMware and Nutanix alternative — full HCI stack, software-defined, scale-out architecture.", url: "/arcfra-spotlight", category: "Products", tags: ["arcfra", "hci", "hyperconverged", "vmware alternative", "nutanix alternative"] },
  { title: "Arcfra ArcStor All-Flash Array", description: "NVMe-oF all-flash storage — sub-100µs latency, HA dual controller.", url: "/arcfra-spotlight", category: "Products", tags: ["arcfra", "all-flash", "nvme", "storage", "array"] },
  { title: "ExaGrid EX Series Backup Appliance", description: "Scale-out backup appliances — 3TB to 3PB, tiered landing zone, ransomware protection.", url: "/exagrid-spotlight", category: "Products", tags: ["exagrid", "backup", "appliance", "ransomware", "dedup"] },
  { title: "Synology DiskStation NAS", description: "Enterprise NAS — 8-bay, 10GbE, PCIe expansion, DiskStation Manager.", url: "/solutions/data-protection", category: "Products", tags: ["synology", "nas", "diskstation", "storage"] },
  { title: "Arcfra Platform Spotlight", description: "Deep-dive into Arcfra HCI — compare vs VMware and Nutanix, see full product lineup.", url: "/arcfra-spotlight", category: "Spotlight", tags: ["arcfra", "spotlight", "hci", "vmware", "nutanix"] },
  { title: "ExaGrid Backup Spotlight", description: "ExaGrid backup storage — tiered landing zone, instant VM recovery, ransomware shield.", url: "/exagrid-spotlight", category: "Spotlight", tags: ["exagrid", "spotlight", "backup", "ransomware"] },
  { title: "Arcfra Migration Assessment", description: "Free VMware and Nutanix migration assessment — get a cost comparison and migration plan.", url: "/arcfra-assessment", category: "Services", tags: ["arcfra", "migration", "vmware", "assessment", "nutanix"] },
  { title: "VMware Alternative Malaysia", description: "Escape Broadcom's VMware price hikes — Arcfra HCI delivers 60% lower TCO, zero vendor lock-in, full local support.", url: "/vmware-alternative-malaysia", category: "Solutions", tags: ["vmware alternative", "vmware replacement", "broadcom", "vmware malaysia", "hci", "arcfra", "nutanix alternative", "vsphere alternative", "vsan alternative"] },
  { title: "Body Worn Camera Malaysia", description: "Hytera body worn cameras for law enforcement, PDRM, government enforcement, and security teams in Malaysia. SC880 5G, VM690 Pro, SC780 4G, DEMS evidence management.", url: "/body-worn-camera-malaysia", category: "Solutions", tags: ["body worn camera", "body camera", "bwc", "bwv", "hytera", "law enforcement", "police camera", "pdrm", "body worn video malaysia", "security camera wearable", "evidence camera"] },
  { title: "Rugged Tablet Malaysia", description: "Honeywell ScanPal EDA10A, RT10 & Dell Pro Rugged 10/12 — MIL-STD-810H, IP65/66, hot-swap batteries for warehouse, logistics & field service in Malaysia.", url: "/rugged-tablet-malaysia", category: "Solutions", tags: ["rugged tablet malaysia", "warehouse tablet", "logistics tablet", "honeywell tablet malaysia", "dell rugged tablet malaysia", "scanpal eda10a", "rt10 tablet", "mil-spec tablet", "forklift tablet", "barcode scanner tablet"] },
  { title: "ExaGrid Backup Malaysia", description: "Ransomware-proof backup appliances — non-network-facing repository, instant VM recovery, 10–50x deduplication, scale-out to 2.7PB. Works with Veeam, Commvault, Veritas. Authorized distributor in KL.", url: "/exagrid-backup-malaysia", category: "Solutions", tags: ["exagrid backup malaysia", "ransomware proof backup", "backup appliance malaysia", "veeam backup malaysia", "instant vm recovery", "enterprise backup malaysia", "ransomware backup protection", "exagrid malaysia", "backup storage malaysia"] },
  // Brands
  { title: "Technology Partners Overview", description: "SE Distribution's authorized distributors (Hytera, Arcfra, ExaGrid) and technology partners.", url: "/brands", category: "Partners", tags: ["brands", "technology partners", "authorized distributor", "hytera", "arcfra", "exagrid", "honeywell", "dell", "synology"] },
  { title: "Hytera Brand Profile", description: "Authorized distributor for Hytera in Malaysia — body cameras, communications, DEMS.", url: "/brands", category: "Partners", tags: ["hytera", "authorized distributor", "body camera", "malaysia"] },
  { title: "Arcfra Brand Profile", description: "Authorized distributor for Arcfra in Malaysia — HCI, all-flash storage, distributed NAS.", url: "/brands", category: "Partners", tags: ["arcfra", "authorized distributor", "hci", "storage", "malaysia"] },
  { title: "ExaGrid Brand Profile", description: "Authorized distributor for ExaGrid in Malaysia — backup appliances, ransomware protection.", url: "/brands", category: "Partners", tags: ["exagrid", "authorized distributor", "backup", "malaysia"] },

  // Industries
  { title: "Industries We Serve", description: "SE Distribution serves law enforcement, government, healthcare, logistics, defence, and more.", url: "/industries", category: "Industries", tags: ["industries", "law enforcement", "government", "healthcare", "logistics", "defence", "manufacturing"] },

  // Company
  { title: "About SE Distribution", description: "SE Distribution Sdn Bhd — authorized distributor and technology partner for enterprise solutions in Malaysia.", url: "/about", category: "Company", tags: ["about", "se distribution", "malaysia", "company", "distributor"] },
  { title: "Contact Us", description: "Get in touch with SE Distribution — sales enquiries, technical support, and solution consultations.", url: "/contact", category: "Company", tags: ["contact", "enquiry", "sales", "support", "phone", "email"] },
  { title: "Case Studies", description: "Real-world deployments — see how SE Distribution has solved challenges for clients across Malaysia.", url: "/case-studies", category: "Company", tags: ["case studies", "deployments", "clients", "projects", "malaysia"] },
];

function scoreResult(result: SearchResult, query: string): number {
  const q = query.toLowerCase().trim();
  const words = q.split(/\s+/);
  let score = 0;

  for (const word of words) {
    if (result.title.toLowerCase().includes(word)) score += 10;
    if (result.description.toLowerCase().includes(word)) score += 5;
    if (result.category.toLowerCase().includes(word)) score += 3;
    for (const tag of result.tags) {
      if (tag.toLowerCase().includes(word)) score += 8;
      if (tag.toLowerCase() === word) score += 4;
    }
  }

  return score;
}

interface SiteSearchProps {
  onClose: () => void;
  onNavigateToSearch?: (query: string) => void;
}

export default function SiteSearch({ onClose, onNavigateToSearch }: SiteSearchProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setActiveIndex(0);
      return;
    }
    const scored = searchIndex
      .map((r) => ({ ...r, score: scoreResult(r, query) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);
    setResults(scored);
    setActiveIndex(0);
  }, [query]);

  const handleSelect = useCallback(
    (url: string) => {
      navigate(url);
      onClose();
    },
    [navigate, onClose]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (results[activeIndex]) {
        handleSelect(results[activeIndex].url);
      } else if (query.trim() && onNavigateToSearch) {
        onNavigateToSearch(query);
      }
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  const categoryColors: Record<string, string> = {
    Solutions: "bg-[#1a1a1a] text-white",
    Products: "bg-[#f5f3ee] text-[#1a1a1a]",
    Spotlight: "bg-[#f5f3ee] text-[#1a1a1a]",
    Partners: "bg-[#f5f3ee] text-[#1a1a1a]",
    Industries: "bg-[#f5f3ee] text-[#1a1a1a]",
    Company: "bg-[#f5f3ee] text-[#1a1a1a]",
    Services: "bg-[#f5f3ee] text-[#1a1a1a]",
  };

  const quickLinks = [
    { label: "Body Worn Camera Malaysia", url: "/body-worn-camera-malaysia", icon: "ri-camera-3-line" },
    { label: "Rugged Tablet Malaysia", url: "/rugged-tablet-malaysia", icon: "ri-smartphone-line" },
    { label: "VMware Alternative Malaysia", url: "/vmware-alternative-malaysia", icon: "ri-server-line" },
    { label: "ExaGrid Backup Malaysia", url: "/exagrid-backup-malaysia", icon: "ri-shield-keyhole-line" },

  ];

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-20 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl w-full max-w-2xl overflow-hidden border border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-search-line text-gray-400 text-lg"></i>
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search products, solutions, brands..."
            className="flex-1 text-sm text-[#1a1a1a] placeholder-gray-400 outline-none bg-transparent"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-[#1a1a1a] transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          )}
          <button
            onClick={onClose}
            className="text-xs text-gray-400 hover:text-[#1a1a1a] transition-colors cursor-pointer whitespace-nowrap ml-1 border border-gray-200 rounded px-1.5 py-0.5"
          >
            ESC
          </button>
        </div>

        {/* Results */}
        {query.trim() ? (
          results.length > 0 ? (
            <div>
              <ul className="max-h-[360px] overflow-y-auto py-2">
                {results.map((result, i) => (
                  <li key={`${result.url}-${result.title}`}>
                    <button
                      onClick={() => handleSelect(result.url)}
                      onMouseEnter={() => setActiveIndex(i)}
                      className={`w-full text-left px-5 py-3.5 flex items-start gap-4 transition-colors cursor-pointer ${
                        i === activeIndex ? "bg-[#f8f7f4]" : "hover:bg-[#fafaf9]"
                      }`}
                    >
                      <div className="w-8 h-8 flex items-center justify-center bg-[#f5f3ee] rounded-lg flex-shrink-0 mt-0.5">
                        <i className="ri-arrow-right-line text-[#1a1a1a] text-sm"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-semibold text-[#1a1a1a] truncate">{result.title}</span>
                          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${categoryColors[result.category] ?? "bg-[#f5f3ee] text-[#1a1a1a]"}`}>
                            {result.category}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed line-clamp-1">{result.description}</p>
                      </div>
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-arrow-right-s-line text-gray-300 text-sm"></i>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
              {onNavigateToSearch && (
                <div className="border-t border-gray-100 px-5 py-3">
                  <button
                    onClick={() => onNavigateToSearch(query)}
                    className="w-full flex items-center justify-center gap-2 text-xs font-semibold text-[#1a1a1a] hover:text-gray-600 transition-colors cursor-pointer py-1"
                  >
                    <i className="ri-search-line text-sm"></i>
                    View all {results.length} results for &ldquo;{query}&rdquo;
                    <i className="ri-arrow-right-line text-sm"></i>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="py-12 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-[#f5f3ee] rounded-full mx-auto mb-3">
                <i className="ri-search-line text-gray-400 text-xl"></i>
              </div>
              <p className="text-sm font-semibold text-[#1a1a1a] mb-1">No results for &ldquo;{query}&rdquo;</p>
              <p className="text-xs text-gray-400">Try searching for a product name, brand, or solution type</p>
            </div>
          )
        ) : (
          <div className="py-5 px-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Quick Links</p>
            <div className="grid grid-cols-2 gap-2">
              {quickLinks.map((link) => (
                <button
                  key={link.url}
                  onClick={() => handleSelect(link.url)}
                  className="flex items-center gap-3 p-3 rounded-lg bg-[#f8f7f4] hover:bg-[#f0ede8] transition-colors cursor-pointer text-left"
                >
                  <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                    <i className={`${link.icon} text-[#1a1a1a] text-base`}></i>
                  </div>
                  <span className="text-xs font-medium text-[#1a1a1a]">{link.label}</span>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-4 text-center">
              Use <kbd className="bg-gray-100 px-1 py-0.5 rounded text-[10px] font-mono">↑</kbd> <kbd className="bg-gray-100 px-1 py-0.5 rounded text-[10px] font-mono">↓</kbd> to navigate, <kbd className="bg-gray-100 px-1 py-0.5 rounded text-[10px] font-mono">Enter</kbd> to select
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
